import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Divider,
  Stack,
  Typography,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Pedido, StatusPedido } from '../../types/pedido';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pedidoService } from '../../services/api';
import { buildComandaHtml } from '../../utils/comandaTemplate';
import { deliveryCourierService, type DeliveryCourier } from '../../services';
import { useToast } from '../../hooks/useToast';

type PedidoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  pedido: Pedido;
  onStatusUpdated?: () => void;
};

const mapFormaPagamentoLabel = (forma?: string): string => {
  if (!forma) return '';
  const value = forma.toLowerCase();
  if (value.includes('pix')) return 'PIX';
  if (value.includes('débito') || value.includes('debito')) return 'DEBITO';
  if (value.includes('crédito') || value.includes('credito')) return 'CREDITO';
  if (value.includes('dinheiro')) return 'DINHEIRO';
  if (value.includes('online_mercado_pago') || value.includes('mercado_pago')) return 'MERCADO PAGO';
  return forma;
};

const getPaymentStatusColor = (status?: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'REJECTED':
    case 'CANCELLED':
    case 'EXPIRED':
      return 'error';
    default:
      return 'default';
  }
};

const getPaymentStatusLabel = (status?: string): string => {
  switch (status) {
    case 'APPROVED':
      return 'Aprovado';
    case 'PENDING':
      return 'Pendente';
    case 'REJECTED':
      return 'Rejeitado';
    case 'CANCELLED':
      return 'Cancelado';
    case 'EXPIRED':
      return 'Expirado';
    default:
      return 'N/A';
  }
};

const getStatusColor = (status: StatusPedido): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case StatusPedido.RECEBIDO:
      return 'info';
    case StatusPedido.EM_PREPARO:
      return 'warning';
    case StatusPedido.PRONTO:
      return 'success';
    case StatusPedido.EM_ROTA:
      return 'primary';
    case StatusPedido.ENTREGUE:
      return 'success';
    case StatusPedido.CANCELADO:
      return 'error';
    default:
      return 'default';
  }
};

const formatCourierPhone = (value?: string | null): string => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

export const PedidoModal = ({ isOpen, onClose, pedido, onStatusUpdated }: PedidoModalProps) => {
  // Validar se o pedido tem todas as propriedades necessárias
  const safePedido: Pedido = {
    id: String(pedido?.id || ''),
    idPedido: String(pedido?.idPedido || ''),
    cliente: String(pedido?.cliente || 'Cliente não informado'),
    telefone: String(pedido?.telefone || ''),
    endereco: pedido?.endereco || { rua: '', numero: '', bairro: '', cidade: '', estado: '', cep: '' },
    itens: Array.isArray(pedido?.itens) ? pedido.itens : [],
    valorItens: Number(pedido?.valorItens || 0),
    valorBrutoItens: Number((pedido as any)?.valorBrutoItens ?? 0),
    descontoCombos: Number((pedido as any)?.descontoCombos ?? 0),
    taxaEntrega: Number(pedido?.taxaEntrega || 0),
    valorTotal: Number(pedido?.valorTotal || 0),
    status: (pedido?.status as StatusPedido) || 'RECEBIDO',
    dataCriacao: String(pedido?.dataCriacao || new Date().toISOString()),
    dataAtualizacao: String(pedido?.dataAtualizacao || new Date().toISOString()),
    observacao: String(pedido?.observacao || ''),
    couponCode: pedido?.couponCode,
    couponDiscount: typeof pedido?.couponDiscount === 'number' ? Number(pedido.couponDiscount) : undefined,
    formaPagamento: pedido?.formaPagamento,
    courierId: pedido?.courierId,
    courierName: pedido?.courierName,
    courierPhone: pedido?.courierPhone,
    paymentStatus: pedido?.paymentStatus,
    paymentMethod: pedido?.paymentMethod,
    paymentId: pedido?.paymentId,
  };
  const hasCouponDiscount =
    typeof safePedido.couponDiscount === 'number' && safePedido.couponDiscount > 0;

  const comboNames = Array.from(
    new Set(
      (safePedido.itens || [])
        .map((item: any) => item?.comboNome)
        .filter((name): name is string => Boolean(name && typeof name === 'string')),
    ),
  );

  const comboTitleSuffix =
    comboNames.length > 0 ? ` - ${comboNames.join(' - ')}` : '';

  const [isPrintPreviewOpen, setIsPrintPreviewOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ 
    open: boolean; 
    message: string; 
    severity: 'success' | 'error' 
  }>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showError, showInfo } = useToast();

  const statusOptions = Object.values(StatusPedido).filter(
    (status) => status !== pedido.status
  );

  const [couriers, setCouriers] = useState<DeliveryCourier[]>([]);
  const [couriersLoading, setCouriersLoading] = useState(false);
  const [selectedCourierId, setSelectedCourierId] = useState<string>(pedido?.courierId ?? '');

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setSelectedCourierId(pedido?.courierId ?? '');
  }, [isOpen, pedido?.courierId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let active = true;

    const fetchCouriers = async () => {
      try {
        setCouriersLoading(true);
        const data = await deliveryCourierService.list({ isActive: true });
        let list = data;

        if (
          safePedido.courierId &&
          !list.some((courier) => courier.id === safePedido.courierId)
        ) {
          list = [
            {
              id: safePedido.courierId,
              name: safePedido.courierName ?? 'Entregador vinculado',
              phone: safePedido.courierPhone ?? null,
              document: null,
              vehicleType: null,
              vehiclePlate: null,
              notes: null,
              isActive: true,
              cnpj: '',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            ...list,
          ];
        }

        if (!active) return;

        setCouriers(list);

        if (!safePedido.courierId && list.length === 1) {
          setSelectedCourierId(list[0].id);
        }
      } catch (error: any) {
        if (!active) return;
        console.error('[PedidoModal] Failed to load couriers', error);
        showError(error?.message || 'Não foi possível carregar os entregadores.');
        setCouriers([]);
      } finally {
        if (active) {
          setCouriersLoading(false);
        }
      }
    };

    void fetchCouriers();

    return () => {
      active = false;
    };
  }, [isOpen, safePedido.courierId, safePedido.courierName, safePedido.courierPhone, showError]);

  const effectiveCourierId = useMemo(() => {
    if (selectedCourierId) {
      return selectedCourierId;
    }
    if (couriers.length === 1) {
      return couriers[0].id;
    }
    return '';
  }, [couriers, selectedCourierId]);

  const selectedCourier = useMemo(() => {
    if (!effectiveCourierId) {
      return null;
    }
    return couriers.find((courier) => courier.id === effectiveCourierId) ?? null;
  }, [couriers, effectiveCourierId]);

  const formatCurrency = (value: number) => {
    try {
      if (typeof value !== 'number' || isNaN(value)) {
        return 'R$ 0,00';
      }
      const formatted = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value);
      return typeof formatted === 'string' ? formatted : 'R$ 0,00';
    } catch (error) {
      console.error('Erro ao formatar moeda:', error);
      return 'R$ 0,00';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      if (!dateString || typeof dateString !== 'string') {
        return 'Data inválida';
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Data inválida';
      }
      const formatted = format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
      return typeof formatted === 'string' ? formatted : 'Data inválida';
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'Data inválida';
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCloseAll = () => {
    setIsPrintPreviewOpen(false);
    onClose();
  };

  const mutation = useMutation({
    mutationFn: ({ status, courierId }: { status: StatusPedido; courierId?: string | null }) =>
      pedidoService.atualizarStatus(safePedido.id, status, courierId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      if (onStatusUpdated) {
        onStatusUpdated();
      }
      setSnackbar({
        open: true,
        message: 'Status atualizado com sucesso!',
        severity: 'success',
      });
      onClose();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || 'Erro ao atualizar o status do pedido.';
      setSnackbar({
        open: true,
        message,
        severity: 'error',
      });
    },
  });

  const handleStatusChange = (novoStatus: StatusPedido) => {
    const requiresCourier = novoStatus === StatusPedido.EM_ROTA;

    if (requiresCourier) {
      if (couriersLoading) {
        showInfo('Aguarde o carregamento dos entregadores.');
        return;
      }

      if (!effectiveCourierId) {
        showInfo('Selecione um entregador ativo para marcar o pedido como EM ROTA.');
        return;
      }

      mutation.mutate({ status: novoStatus, courierId: effectiveCourierId });
      return;
    }

    mutation.mutate({ status: novoStatus });
  };

  const handlePrintComanda = () => {
    setIsPrintPreviewOpen(true);
  };

  const handleConfirmPrint = () => {
    if (typeof window === 'undefined') return;

    const printWindow = window.open('', '_blank', 'width=600,height=800');
    if (!printWindow) return;

    const html = buildComandaHtml(safePedido, formatCurrency, formatDate);

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    const handlePrintAndClose = () => {
      try {
        printWindow.print();
      } finally {
        printWindow.close();
        setIsPrintPreviewOpen(false);
      }
    };

    if (printWindow.document.readyState === 'complete') {
      handlePrintAndClose();
    } else {
      printWindow.onload = handlePrintAndClose;
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleCloseAll}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Detalhes do Pedido #{safePedido.idPedido}</Typography>
            <IconButton onClick={handleCloseAll} aria-label="fechar">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Box>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Informações do Cliente
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">
                  <strong>Nome:</strong> {safePedido.cliente}
                </Typography>
                <Typography variant="body2">
                  <strong>Telefone:</strong> {safePedido.telefone}
                </Typography>
                <Typography variant="body2">
                  <strong>Endereço:</strong> {safePedido.endereco?.rua || ''}, {safePedido.endereco?.numero || ''}
                </Typography>
                <Typography variant="body2">
                  {safePedido.endereco?.complemento && `${safePedido.endereco.complemento} - `}
                  {safePedido.endereco?.bairro || ''}
                </Typography>
                <Typography variant="body2">
                  {safePedido.endereco?.cidade || ''} - {safePedido.endereco?.estado || ''}
                </Typography>
              </CardContent>
            </Card>

            {safePedido.formaPagamento && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    <strong>Forma de pagamento:</strong> {mapFormaPagamentoLabel(safePedido.formaPagamento)}
                  </Typography>
                  {(safePedido.formaPagamento?.toLowerCase().includes('online_mercado_pago') || 
                    safePedido.formaPagamento?.toLowerCase().includes('mercado_pago')) && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        <strong>Status do pagamento:</strong>
                      </Typography>
                      <Chip
                        label={safePedido.paymentStatus ? getPaymentStatusLabel(safePedido.paymentStatus) : 'VERIFICAR'}
                        color={safePedido.paymentStatus ? getPaymentStatusColor(safePedido.paymentStatus) : 'error'}
                        size="small"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {`Itens do Pedido${comboTitleSuffix}`}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <List dense>
                  {safePedido.itens?.map((item, index) => (
                    <ListItem key={index} disableGutters>
                      <ListItemText
                        primary={`${item?.quantidade || 0}x ${item?.nome || 'Item sem nome'}`}
                        secondary={item?.observacao || ''}
                      />
                      <ListItemSecondaryAction>
                        <Typography variant="body2">
                          {formatCurrency((item?.preco || 0) * (item?.quantidade || 0))}
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItem>
                  )) || []}
                </List>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Valor bruto dos itens:
                  </Typography>
                  <Typography variant="body2">{formatCurrency(safePedido.valorBrutoItens)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Valor dos itens:
                  </Typography>
                  <Typography variant="body2">{formatCurrency(safePedido.valorItens)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Desconto combos:
                  </Typography>
                  <Typography variant="body2" color={safePedido.descontoCombos > 0 ? 'error.main' : 'text.primary'}>
                    {safePedido.descontoCombos > 0
                      ? `${formatCurrency(safePedido.descontoCombos)}`
                      : formatCurrency(0)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Taxa de entrega:
                  </Typography>
                  <Typography variant="body2">{formatCurrency(safePedido.taxaEntrega)}</Typography>
                </Box>
                {hasCouponDiscount && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="body2" color="error.main">
                      Desconto de cupom{safePedido.couponCode ? ` (${safePedido.couponCode})` : ''}:
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      -{formatCurrency(safePedido.couponDiscount || 0)}
                    </Typography>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                  <Typography variant="subtitle1">Total:</Typography>
                  <Typography variant="h6">
                    {formatCurrency(safePedido.valorTotal)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Status do Pedido
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box mb={3}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Status Atual:
                  </Typography>
                  <Chip
                    label={safePedido.status.replace('_', ' ')}
                    color={getStatusColor(safePedido.status)}
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Data do Pedido:
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(safePedido.dataCriacao)}
                  </Typography>

                  {safePedido.observacao && (
                    <>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Observações:
                      </Typography>
                      <Typography variant="body1">
                        {safePedido.observacao}
                      </Typography>
                    </>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Entregador
                </Typography>

                {safePedido.courierName && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Atual: {safePedido.courierName}
                    {safePedido.courierPhone
                      ? ` • ${formatCourierPhone(safePedido.courierPhone)}`
                      : ''}
                  </Typography>
                )}

                {couriersLoading ? (
                  <Box display="flex" alignItems="center" gap={1} py={1}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Carregando entregadores...
                    </Typography>
                  </Box>
                ) : couriers.length === 0 ? (
                  <Alert severity="info" variant="outlined">
                    Cadastre um entregador ativo para habilitar o status EM ROTA.
                  </Alert>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel id="courier-select-label">Selecionar entregador</InputLabel>
                    <Select
                      labelId="courier-select-label"
                      value={selectedCourierId || (couriers.length === 1 ? couriers[0].id : '')}
                      label="Selecionar entregador"
                      onChange={(event) => setSelectedCourierId(event.target.value)}
                    >
                      {couriers.length > 1 && (
                        <MenuItem value="">
                          <em>Selecione</em>
                        </MenuItem>
                      )}
                      {couriers.map((courier) => (
                        <MenuItem key={courier.id} value={courier.id}>
                          {courier.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {selectedCourier
                        ? selectedCourier.phone
                          ? `Telefone: ${formatCourierPhone(selectedCourier.phone)}`
                          : 'Telefone não informado.'
                        : couriers.length === 1
                        ? 'O entregador será aplicado automaticamente ao marcar EM ROTA.'
                        : 'Selecione o entregador responsável antes de marcar EM ROTA.'}
                    </FormHelperText>
                  </FormControl>
                )}

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Alterar Status:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {statusOptions.map((status) => (
                    <Button
                      key={status}
                      variant="contained"
                      size="small"
                      onClick={() => handleStatusChange(status)}
                      disabled={
                        mutation.isPending ||
                        (status === StatusPedido.EM_ROTA &&
                          (couriersLoading || couriers.length === 0 || !effectiveCourierId))
                      }
                      startIcon={mutation.isPending ? <CircularProgress size={16} /> : null}
                      sx={{ mb: 1 }}
                    >
                      {status.replace('_', ' ')}
                    </Button>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handlePrintComanda} color="primary" variant="contained">
            Imprimir comanda
          </Button>
          <Button onClick={handleCloseAll} color="inherit">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isPrintPreviewOpen}
        onClose={handleCloseAll}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Pré-visualização da comanda</Typography>
            <IconButton onClick={handleCloseAll} aria-label="fechar">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            id="comanda-print-area"
            sx={{
              width: '80mm',
              maxWidth: '100%',
              bgcolor: 'background.paper',
              p: 1,
              border: '1px dashed',
              borderColor: 'grey.400',
              mx: 'auto',
              fontSize: '0.75rem',
            }}
          >
            <Typography variant="subtitle2" align="center" gutterBottom>
              Comanda Pedido #{safePedido.idPedido}
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Typography variant="caption" fontWeight="bold">
              Cliente
            </Typography>
            <Typography variant="body2">{safePedido.cliente}</Typography>
            <Typography variant="body2">Tel: {safePedido.telefone || '-'}</Typography>
            <Typography variant="body2">
              {safePedido.endereco?.rua || ''}, {safePedido.endereco?.numero || ''}
            </Typography>
            <Typography variant="body2">
              {safePedido.endereco?.bairro || ''} - {safePedido.endereco?.cidade || ''}
            </Typography>
            {safePedido.endereco?.complemento && (
              <Typography variant="body2">
                Ref.: {safePedido.endereco.complemento}
              </Typography>
            )}

            {safePedido.formaPagamento && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'bold', color: 'text.primary' }}
                >
                  Pagamento: {mapFormaPagamentoLabel(safePedido.formaPagamento)}
                </Typography>
                <Divider sx={{ my: 1 }} />
              </>
            )}

            {!safePedido.formaPagamento && <Divider sx={{ my: 1 }} />}

            <Typography variant="caption" fontWeight="bold">
              Itens
            </Typography>
            <List dense>
              {safePedido.itens?.map((item, index) => (
                <ListItem key={index} disableGutters sx={{ py: 0.25 }}>
                  <ListItemText
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                    primary={`${item?.quantidade || 0}x ${item?.nome || 'Item sem nome'}`}
                    secondary={item?.observacao || ''}
                  />
                  <ListItemSecondaryAction>
                    <Typography variant="body2">
                      {formatCurrency((item?.preco || 0) * (item?.quantidade || 0))}
                    </Typography>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 1 }} />

            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption">Valor dos Itens:</Typography>
              <Typography variant="caption">{formatCurrency(safePedido.valorItens)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption">Taxa de Entrega:</Typography>
              <Typography variant="caption">{formatCurrency(safePedido.taxaEntrega)}</Typography>
            </Box>
            {hasCouponDiscount && (
              <Box display="flex" justifyContent="space-between" mt={0.25}>
                <Typography variant="caption" color="error.main">
                  Desconto de cupom{safePedido.couponCode ? ` (${safePedido.couponCode})` : ''}:
                </Typography>
                <Typography variant="caption" color="error.main">
                  -{formatCurrency(safePedido.couponDiscount || 0)}
                </Typography>
              </Box>
            )}
            <Box display="flex" justifyContent="space-between" mt={0.5}>
              <Typography variant="caption" fontWeight="bold">
                Total:
              </Typography>
              <Typography variant="caption" fontWeight="bold">
                {formatCurrency(safePedido.valorTotal)}
              </Typography>
            </Box>

            {safePedido.observacao && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" fontWeight="bold">
                  Observações
                </Typography>
                <Typography variant="body2">{safePedido.observacao}</Typography>
              </>
            )}

            <Divider sx={{ my: 1 }} />
            <Typography variant="caption">Status: {safePedido.status.replace('_', ' ')}</Typography>
            <Typography variant="caption" display="block">
              Data: {formatDate(safePedido.dataCriacao)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleConfirmPrint} variant="contained" color="primary">
            Imprimir
          </Button>
          <Button onClick={handleCloseAll} color="inherit">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
